// Sort configuration for category resource lists.
//
// Each sort option is `{ key, label, comparator }` where `comparator(a, b)`
// returns the usual negative/zero/positive number for Array.prototype.sort.
//
// To enable sorting for a new category:
//   1. Add an entry to SORT_CONFIG below with its `general` and `specific` options.
//      Reuse `titleSort(...)` + `COMMON_GENERAL_OPTIONS` for the General group.
//   2. Make sure the category's list projection in
//      backend/routes/fetchContent.py includes the fields the comparators read.
// The SortByMenu component and the List wiring are category-agnostic, so nothing
// else needs to change.
//
// Visibility note: get_people_documents flattens a member's per-vertical `show`
// onto the root `show` field, so visibility comparators can read `item.show`
// uniformly for every category (including People).

import { byString, byNumber, num, date } from './sortComparators';

// --- comparator helpers -----------------------------------------------------

// Visibility score: visible (show !== false) ranks above hidden.
const visScore = (item) => (item?.show === false ? 0 : 1);

// --- reusable General building blocks ---------------------------------------

// Leading A-Z / Z-A sort on a category's primary name/title field.
const titleSort = (label, getField) => [
  { key: 'title-asc', label: `${label} (A-Z)`, comparator: byString(getField, 'asc') },
  { key: 'title-desc', label: `${label} (Z-A)`, comparator: byString(getField, 'desc') },
];

// Shared General options that are identical for every category.
const COMMON_GENERAL_OPTIONS = [
  { key: 'recently-added', label: 'Recently Added', comparator: byNumber(date('created_at'), 'desc') },
  { key: 'oldest-added', label: 'Oldest Added', comparator: byNumber(date('created_at'), 'asc') },
  {
    key: 'visible-first',
    label: 'Show Visible to Members First',
    comparator: (a, b) => visScore(b) - visScore(a),
  },
  {
    key: 'hidden-first',
    label: 'Show Hidden from Members First',
    comparator: (a, b) => visScore(a) - visScore(b),
  },
];

// --- per-category configuration ---------------------------------------------

export const SORT_CONFIG = {
  papers: {
    general: [...titleSort('Title', (i) => i.title), ...COMMON_GENERAL_OPTIONS],
    specific: [
      { key: 'citation-high-low', label: 'Citation Count (High-Low)', comparator: byNumber(num('citationCount'), 'desc') },
      { key: 'citation-low-high', label: 'Citation Count (Low-High)', comparator: byNumber(num('citationCount'), 'asc') },
      { key: 'pubyear-new-old', label: 'Publication Year (Newest-Oldest)', comparator: byNumber(date('publicationDate'), 'desc') },
      { key: 'pubyear-old-new', label: 'Publication Year (Oldest-Newest)', comparator: byNumber(date('publicationDate'), 'asc') },
      { key: 'journal-asc', label: 'Journal Name (A-Z)', comparator: byString((i) => i.venue, 'asc') },
      { key: 'journal-desc', label: 'Journal Name (Z-A)', comparator: byString((i) => i.venue, 'desc') },
    ],
  },

  patents: {
    general: [...titleSort('Title', (i) => i.title), ...COMMON_GENERAL_OPTIONS],
    specific: [
      { key: 'filing-new-old', label: 'Filing Date (Newest-Oldest)', comparator: byNumber(date('filing_date'), 'desc') },
      { key: 'filing-old-new', label: 'Filing Date (Oldest-Newest)', comparator: byNumber(date('filing_date'), 'asc') },
      { key: 'published-new-old', label: 'Date Published (Newest-Oldest)', comparator: byNumber(date('publication_date'), 'desc') },
      { key: 'published-old-new', label: 'Date Published (Oldest-Newest)', comparator: byNumber(date('publication_date'), 'asc') },
    ],
  },

  // Funding. Title maps to OpportunityTitle; "Organization Name" maps to the
  // funding-source AgencyName; funding amount uses AwardCeiling for both extremes.
  grants: {
    general: [...titleSort('Title', (i) => i.OpportunityTitle), ...COMMON_GENERAL_OPTIONS],
    specific: [
      { key: 'deadline-soonest', label: 'Deadline Soonest', comparator: byNumber(date('CloseDate'), 'asc') },
      { key: 'funding-largest', label: 'Largest Funding Amount', comparator: byNumber(num('AwardCeiling'), 'desc') },
      { key: 'funding-smallest', label: 'Smallest Funding Amount', comparator: byNumber(num('AwardCeiling'), 'asc') },
      { key: 'org-asc', label: 'Organization Name (A-Z)', comparator: byString((i) => i.AgencyName, 'asc') },
      { key: 'org-desc', label: 'Organization Name (Z-A)', comparator: byString((i) => i.AgencyName, 'desc') },
    ],
  },

  people: {
    general: [...titleSort('Name', (i) => i.name), ...COMMON_GENERAL_OPTIONS],
    specific: [],
  },

  // Jobs. "Position Title" maps to title; "Organization Name" maps to company.
  jobs: {
    general: [...titleSort('Position Title', (i) => i.title), ...COMMON_GENERAL_OPTIONS],
    specific: [
      { key: 'posting-new-old', label: 'Posting Date (Newest-Oldest)', comparator: byNumber(date('posting_date'), 'desc') },
      { key: 'posting-old-new', label: 'Posting Date (Oldest-Newest)', comparator: byNumber(date('posting_date'), 'asc') },
      { key: 'org-asc', label: 'Organization Name (A-Z)', comparator: byString((i) => i.company, 'asc') },
      { key: 'org-desc', label: 'Organization Name (Z-A)', comparator: byString((i) => i.company, 'desc') },
      { key: 'location-asc', label: 'Location (A-Z)', comparator: byString((i) => i.location, 'asc') },
      { key: 'location-desc', label: 'Location (Z-A)', comparator: byString((i) => i.location, 'desc') },
    ],
  },

  organizations: {
    general: [...titleSort('Name', (i) => i.name), ...COMMON_GENERAL_OPTIONS],
    specific: [
      { key: 'location-asc', label: 'Location (A-Z)', comparator: byString((i) => i.location, 'asc') },
      { key: 'location-desc', label: 'Location (Z-A)', comparator: byString((i) => i.location, 'desc') },
    ],
  },

  events: {
    general: [...titleSort('Name', (i) => i.name), ...COMMON_GENERAL_OPTIONS],
    specific: [
      { key: 'event-date-soonest', label: 'Event Date (Soonest-Latest)', comparator: byNumber(date('date'), 'asc') },
      { key: 'event-date-latest', label: 'Event Date (Latest-Soonest)', comparator: byNumber(date('date'), 'desc') },
      { key: 'reg-deadline-soonest', label: 'Registration Deadline (Soonest-Latest)', comparator: byNumber(date('registration_deadline'), 'asc') },
      { key: 'reg-deadline-latest', label: 'Registration Deadline (Latest-Soonest)', comparator: byNumber(date('registration_deadline'), 'desc') },
      { key: 'location-asc', label: 'Location (A-Z)', comparator: byString((i) => i.location, 'asc') },
      { key: 'location-desc', label: 'Location (Z-A)', comparator: byString((i) => i.location, 'desc') },
    ],
  },
};

// Returns `{ general, specific }` for a category, or null when the category has
// no sort configuration yet (so the Sort by button stays hidden).
export const getSortOptions = (category) => {
  const config = SORT_CONFIG[category];
  if (!config) return null;
  return {
    general: config.general,
    specific: config.specific,
  };
};
